import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogTrigger 
} from "../components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Trash2, 
  Download, 
  Database, 
  AlertTriangle, 
  RefreshCw,
  Users,
  FileText,
  Award,
  BookOpen,
  Building,
  Loader2,
  CheckCircle,
  Shield,
  Mail,
  Send
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/utils";
import DashboardLayout from "../components/DashboardLayout";

const ADMIN_PASSWORD = "CredentiaAdmin2024!";

export default function Maintenance() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchCandidates();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get("/maintenance/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      // Set default stats if endpoint doesn't exist
      setStats({
        candidates: 0,
        competencies: 0,
        institutions: 0,
        ipcbs: 0,
        awards: 0,
        scores: 0,
        documents: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await api.get("/candidates");
      setCandidates(response.data);
    } catch (error) {
      console.error("Failed to fetch candidates:", error);
    }
  };

  const handlePurgeAction = (action) => {
    setCurrentAction(action);
    setConfirmDialogOpen(true);
  };

  const executeAction = async () => {
    if (adminPassword !== ADMIN_PASSWORD) {
      toast.error("Invalid admin password");
      return;
    }

    setProcessing(true);
    try {
      let endpoint = "";
      let method = "delete";
      
      switch (currentAction.type) {
        case "purge_all_candidates":
          endpoint = "/maintenance/purge/candidates";
          break;
        case "purge_all_scores":
          endpoint = "/maintenance/purge/scores";
          break;
        case "purge_all_competencies":
          endpoint = "/maintenance/purge/competencies";
          break;
        case "purge_selected_candidates":
          endpoint = "/maintenance/purge/candidates/selected";
          method = "post";
          break;
        case "reset_candidate_analysis":
          endpoint = "/maintenance/reset/analysis";
          method = "post";
          break;
        case "backup_database":
          endpoint = "/maintenance/backup";
          method = "get";
          break;
        default:
          throw new Error("Unknown action");
      }

      if (method === "delete") {
        await api.delete(endpoint, { data: { password: adminPassword } });
      } else if (method === "post") {
        await api.post(endpoint, { 
          password: adminPassword,
          candidate_ids: selectedItems 
        });
      } else {
        // Backup - download file
        const response = await api.get(endpoint, { 
          responseType: 'blob',
          params: { password: adminPassword }
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `credentia_backup_${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      toast.success(`${currentAction.label} completed successfully`);
      setConfirmDialogOpen(false);
      setAdminPassword("");
      setSelectedItems([]);
      fetchStats();
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setProcessing(false);
    }
  };

  const toggleCandidateSelection = (candidateId) => {
    setSelectedItems(prev => 
      prev.includes(candidateId) 
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const selectAllCandidates = () => {
    const filtered = filteredCandidates.map(c => c.candidate_id);
    setSelectedItems(filtered);
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const filteredCandidates = candidates.filter(c => 
    filterStatus === "all" || c.case_status === filterStatus
  );

  const handleTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await api.post("/email/test");
      toast.success(response.data.message || "Test email sent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const statCards = [
    { key: "candidates", label: "Candidates", icon: Users, color: "bg-blue-500" },
    { key: "competencies", label: "Competencies", icon: BookOpen, color: "bg-green-500" },
    { key: "institutions", label: "Institutions", icon: Building, color: "bg-purple-500" },
    { key: "ipcbs", label: "IPCBs", icon: Award, color: "bg-amber-500" },
    { key: "awards", label: "Awards", icon: Award, color: "bg-pink-500" },
    { key: "scores", label: "Scores", icon: FileText, color: "bg-indigo-500" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="maintenance-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Database Maintenance</h1>
            <p className="text-slate-500 mt-1">Manage, purge, and backup system data</p>
          </div>
          <Button variant="outline" onClick={fetchStats} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Stats
          </Button>
        </div>

      {/* Database Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.key}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat.color}`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats[stat.key] || 0}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="purge" className="space-y-4">
        <TabsList>
          <TabsTrigger value="purge" data-testid="tab-purge">
            <Trash2 className="w-4 h-4 mr-2" />
            Purge Data
          </TabsTrigger>
          <TabsTrigger value="selective" data-testid="tab-selective">
            <Users className="w-4 h-4 mr-2" />
            Selective Delete
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">
            <Download className="w-4 h-4 mr-2" />
            Backup
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Email Test
          </TabsTrigger>
        </TabsList>

        {/* Purge Tab */}
        <TabsContent value="purge">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Bulk Purge Operations
              </CardTitle>
              <CardDescription>
                These actions will permanently delete data. This cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Purge All Candidates */}
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Users className="w-8 h-8 text-red-600" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-red-800">Purge All Candidates</h3>
                        <p className="text-sm text-red-600 mb-3">
                          Delete all candidates, their documents, scores, and analysis data.
                        </p>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handlePurgeAction({
                            type: "purge_all_candidates",
                            label: "Purge All Candidates",
                            warning: "This will delete ALL candidates and their associated data."
                          })}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Purge Candidates
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Purge All Scores */}
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <FileText className="w-8 h-8 text-amber-600" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-amber-800">Purge All Scores</h3>
                        <p className="text-sm text-amber-600 mb-3">
                          Delete all assessment scores. Candidates will need re-scoring.
                        </p>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="border-amber-500 text-amber-700 hover:bg-amber-100"
                          onClick={() => handlePurgeAction({
                            type: "purge_all_scores",
                            label: "Purge All Scores",
                            warning: "This will delete ALL scores across all candidates."
                          })}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Purge Scores
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Purge All Competencies */}
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <BookOpen className="w-8 h-8 text-purple-600" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-purple-800">Purge All Competencies</h3>
                        <p className="text-sm text-purple-600 mb-3">
                          Delete all competency frameworks. Will need to recreate.
                        </p>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="border-purple-500 text-purple-700 hover:bg-purple-100"
                          onClick={() => handlePurgeAction({
                            type: "purge_all_competencies",
                            label: "Purge All Competencies",
                            warning: "This will delete ALL competencies across all levels."
                          })}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Purge Competencies
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Reset All Analysis */}
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <RefreshCw className="w-8 h-8 text-blue-600" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-blue-800">Reset All Analysis</h3>
                        <p className="text-sm text-blue-600 mb-3">
                          Clear AI analysis from all candidates. Documents kept.
                        </p>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="border-blue-500 text-blue-700 hover:bg-blue-100"
                          onClick={() => handlePurgeAction({
                            type: "reset_candidate_analysis",
                            label: "Reset All Analysis",
                            warning: "This will clear AI analysis from all candidates."
                          })}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Reset Analysis
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Selective Delete Tab */}
        <TabsContent value="selective">
          <Card>
            <CardHeader>
              <CardTitle>Selective Candidate Management</CardTitle>
              <CardDescription>
                Select specific candidates to delete or reset
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="documents_uploaded">Documents Uploaded</SelectItem>
                    <SelectItem value="ai_review">Credentia Review</SelectItem>
                    <SelectItem value="assessor_review">Assessor Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button variant="outline" size="sm" onClick={selectAllCandidates}>
                  Select All ({filteredCandidates.length})
                </Button>
                <Button variant="outline" size="sm" onClick={clearSelection}>
                  Clear Selection
                </Button>
                
                {selectedItems.length > 0 && (
                  <Badge variant="secondary">{selectedItems.length} selected</Badge>
                )}
              </div>

              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                {filteredCandidates.map((candidate) => (
                  <div 
                    key={candidate.candidate_id}
                    className={`flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50 ${
                      selectedItems.includes(candidate.candidate_id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <Checkbox
                      checked={selectedItems.includes(candidate.candidate_id)}
                      onCheckedChange={() => toggleCandidateSelection(candidate.candidate_id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{candidate.full_name}</p>
                      <p className="text-sm text-slate-500">{candidate.email}</p>
                    </div>
                    <Badge variant="outline">{candidate.case_status}</Badge>
                  </div>
                ))}
                {filteredCandidates.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    No candidates found
                  </div>
                )}
              </div>

              {selectedItems.length > 0 && (
                <div className="flex gap-3 mt-4">
                  <Button 
                    variant="destructive"
                    onClick={() => handlePurgeAction({
                      type: "purge_selected_candidates",
                      label: `Delete ${selectedItems.length} Candidates`,
                      warning: `This will permanently delete ${selectedItems.length} selected candidates.`
                    })}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected ({selectedItems.length})
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Database Backup
              </CardTitle>
              <CardDescription>
                Download a backup of your database for safekeeping
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Download className="w-8 h-8 text-green-600" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-green-800">Full Database Backup</h3>
                        <p className="text-sm text-green-600 mb-3">
                          Download all candidates, competencies, scores, and settings as JSON.
                        </p>
                        <Button 
                          className="bg-green-600 hover:bg-green-700"
                          size="sm"
                          onClick={() => handlePurgeAction({
                            type: "backup_database",
                            label: "Backup Database",
                            warning: "This will download a full backup of the database."
                          })}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Backup
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Test Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Email Notification Test
              </CardTitle>
              <CardDescription>
                Test email notifications to verify your Resend integration is working
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Send className="w-8 h-8 text-blue-600" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-blue-800">Send Test Email</h3>
                        <p className="text-sm text-blue-600 mb-3">
                          Send a test approval email to your logged-in email address.
                        </p>
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700"
                          size="sm"
                          onClick={handleTestEmail}
                          disabled={sendingTestEmail}
                          data-testid="send-test-email-btn"
                        >
                          {sendingTestEmail ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Test Email
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-slate-800 mb-3">Email Notification Triggers</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span><strong>C.</strong> Assessor decision → IQA notified</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span><strong>D.</strong> IQA Approved → Candidate notified</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span><strong>E.</strong> IQA Returned → Assessor notified</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span><strong>F.</strong> IQA Rejected → Candidate notified</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span><strong>G.</strong> Reports generated → Candidate notified</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Email Configuration</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Emails are sent from <strong>noreply@credentiaai.com</strong> using Resend.
                        Make sure your domain is verified in Resend dashboard.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Shield className="w-5 h-5" />
              Confirm Action: {currentAction?.label}
            </DialogTitle>
            <DialogDescription>
              {currentAction?.warning}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700 font-medium">
                This action requires admin authentication.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin-pwd">Admin Password</Label>
              <Input
                id="admin-pwd"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="Enter admin password"
              />
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setConfirmDialogOpen(false);
                  setAdminPassword("");
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={executeAction}
                disabled={processing || !adminPassword}
                className="flex-1"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
}
